import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import api from '@/lib/api';

import { RequestHeader } from './RequestDetail/RequestHeader';
import { RequestInfoCard } from './RequestDetail/RequestInfoCard';
import { RequestItemsTable } from './RequestDetail/RequestItemsTable';
import { ReviewPanel, CancellationPanel, CancellationPendingBadge, DeliveryConfirmedBadge, DeliveryPanel, SponsorApprovalPanel, DeleteRequestButton } from './RequestDetail/ActionPanels';
import { ReviewNotesCard, HistoryCard } from './RequestDetail/HistoryCard';

export default function RequestDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewItems, setReviewItems] = useState([]);
  const [internalNote, setInternalNote] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmedItems, setConfirmedItems] = useState({});

  const load = async () => {
    try {
      const r = await api.get(`/api/requests/${id}`);
      setRequest(r.data);
      setInternalNote(r.data.internal_note || '');
      setReviewItems(
        (r.data.items || []).map(item => ({
          item_id: item.id,
          approved_quantity: item.approved_quantity ?? item.requested_quantity,
          reviewer_note: item.reviewer_note || '',
        }))
      );
    } catch { toast.error('Failed to load request'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  // Derived state
  const isReviewer = hasPerm('review_requests');
  const canReview = isReviewer && ['submitted', 'under_review'].includes(request?.request_status);
  const isDelivered = request?.delivery_confirmed;
  const canCancel = ['approved', 'partially_approved'].includes(request?.request_status) && !isDelivered;
  const isCancellationPending = request?.request_status === 'cancellation_requested';
  const canCancelItems = hasPerm('cancel_requests') && ['approved', 'partially_approved'].includes(request?.request_status) && !isDelivered;
  const canConfirmDelivery = isReviewer && ['approved', 'partially_approved'].includes(request?.request_status) && !isDelivered;
  const isPendingSponsorApproval = request?.request_status === 'pending_sponsor_approval';

  // Handlers
  const updateReviewItem = (idx, field, value) => {
    setReviewItems(prev => prev.map((ri, i) => i === idx ? { ...ri, [field]: value } : ri));
  };

  const toggleConfirm = (idx) => setConfirmedItems(prev => ({ ...prev, [idx]: !prev[idx] }));

  const handleReview = () => {
    const unchecked = request.items.filter((_, idx) => !confirmedItems[idx]);
    if (unchecked.length > 0) {
      toast.error(`${unchecked.map(i => i.right_name || 'Item').join(', ')} ${t('needs_confirmation') || 'needs to be confirmed before sending'}`);
      return;
    }
    const lines = [];
    const approvedItems = [];
    const rejectedItems = [];
    const partialItems = [];
    request.items.forEach((item, idx) => {
      const aq = reviewItems[idx]?.approved_quantity ?? item.requested_quantity;
      const rq = item.requested_quantity;
      if (aq === 0) rejectedItems.push(item.right_name);
      else if (aq >= rq) approvedItems.push(`${item.right_name} (${aq})`);
      else partialItems.push(`${item.right_name} (${aq}/${rq})`);
    });
    if (approvedItems.length > 0) lines.push(`Approved: ${approvedItems.join(', ')}`);
    if (partialItems.length > 0) lines.push(`Partially Approved: ${partialItems.join(', ')}`);
    if (rejectedItems.length > 0) lines.push(`Rejected: ${rejectedItems.join(', ')}`);
    setConfirmAction({
      title: 'Confirm Decision', desc: lines.join('\n') || 'Review all items', confirmText: 'Confirm',
      fn: async () => {
        setSubmittingReview(true);
        try { await api.put(`/api/requests/${id}/review`, { items: reviewItems, internal_note: internalNote }); toast.success(t('decision_sent')); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
        setSubmittingReview(false);
      }
    });
  };

  const handleCancel = () => {
    const matchInfo = request.match ? `${request.match.home_team} vs ${request.match.away_team}` : '';
    const itemSummary = request.items?.length ? `${request.items.length} item(s)` : '';
    const msg = `You are about to cancel Request #${request.request_number} for ${matchInfo}${itemSummary ? ` with ${itemSummary}` : ''}. This action will request cancellation.`;
    setConfirmAction({ title: 'Cancel Request', desc: msg, confirmText: 'Yes, Cancel', destructive: true, fn: async () => {
      try { const r = await api.put(`/api/requests/${id}/cancel`); toast.success(r.data.message || t('request_cancelled')); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
      setConfirmAction(null);
    }});
  };

  const handleApproveCancellation = () => {
    setConfirmAction({ title: 'Approve Cancellation', desc: `You are about to approve the cancellation for Request #${request.request_number}. All approved items will be cancelled and balances restored.`, confirmText: 'Approve', destructive: true, fn: async () => {
      try { await api.put(`/api/requests/${id}/approve-cancellation`); toast.success(t('cancellation_approved')); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      setConfirmAction(null);
    }});
  };

  const handleRejectCancellation = () => {
    setConfirmAction({ title: 'Reject Cancellation', desc: `You are about to reject the cancellation request for Request #${request.request_number}. The request will return to its approved state.`, confirmText: 'Reject', fn: async () => {
      try { await api.put(`/api/requests/${id}/reject-cancellation`); toast.success(t('cancellation_rejected')); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      setConfirmAction(null);
    }});
  };

  const handleDeleteRequest = () => {
    setConfirmAction({ title: 'Delete Request Permanently', desc: 'This will permanently delete this request and all its items. This action cannot be undone.', confirmText: 'Delete Forever', destructive: true, fn: async () => {
      try { await api.delete(`/api/requests/${id}`); toast.success('Request deleted'); navigate('/requests'); }
      catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
      setConfirmAction(null);
    }});
  };

  const handleConfirmDelivery = () => {
    const matchInfo = request.match ? `${request.match.home_team} vs ${request.match.away_team}` : '';
    const itemNames = request.items?.map(i => i.right_name).filter(Boolean).join(', ') || '';
    setConfirmAction({ title: 'Confirm Delivery', desc: `You are about to confirm delivery for Request #${request.request_number} (${matchInfo}). Items: ${itemNames || 'all approved items'}. Cancellation will be disabled after confirmation.`, confirmText: 'Confirm Delivery', fn: async () => {
      setConfirmingDelivery(true);
      try { await api.put(`/api/requests/${id}/confirm-delivery`, { delivery_note: deliveryNote }); toast.success(t('delivery_confirmed')); setDeliveryNote(''); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      setConfirmingDelivery(false);
      setConfirmAction(null);
    }});
  };

  const handleSponsorApprove = (approved) => {
    const msg = approved ? t('confirm_sponsor_approve') : t('confirm_sponsor_reject');
    setConfirmAction({ title: approved ? 'Approve Request' : 'Reject Request', desc: msg, confirmText: approved ? 'Approve' : 'Reject', destructive: !approved, fn: async () => {
      try { await api.put(`/api/requests/${id}/sponsor-approve`, { approved, sponsor_note: internalNote }); toast.success(approved ? t('request_approved_by_sponsor') : t('request_rejected_by_sponsor')); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      setConfirmAction(null);
    }});
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const lang = i18n.language || 'en';
      const response = await api.get(`/api/requests/${id}/pdf?lang=${lang}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Request_${request.request_number || 'detail'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('pdf_exported') || 'PDF exported');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">{t('loading')}</div>;
  if (!request) return <div className="text-center py-20 text-gray-400 text-sm">Request not found</div>;

  return (
    <div data-testid="request-detail-page" className="space-y-5 animate-fade-in max-w-4xl">
      <RequestHeader request={request} exporting={exporting} onExportPDF={exportPDF} />

      <Card className="bg-white border-0 rounded-card shadow-card">
        <CardContent className="p-0">
          <RequestInfoCard request={request} />
          <RequestItemsTable request={request} canReview={canReview} reviewItems={reviewItems}
            confirmedItems={confirmedItems} onUpdateReviewItem={updateReviewItem} onToggleConfirm={toggleConfirm}
            canCancelItems={canCancelItems} onItemCancelled={load} />
        </CardContent>
      </Card>

      {canReview && <ReviewPanel internalNote={internalNote} onNoteChange={setInternalNote} onReview={handleReview} submitting={submittingReview} canCancel={user?.role !== 'account_manager' && canCancel} onCancel={handleCancel} />}

      {isReviewer && !canReview && canCancel && !isCancellationPending && user?.role !== 'account_manager' && (
        <Button data-testid="manager-cancel-btn" variant="destructive" onClick={handleCancel} className="text-sm h-9">{t('cancel_request')}</Button>
      )}

      {isReviewer && isCancellationPending && <CancellationPanel onApproveCancellation={handleApproveCancellation} onRejectCancellation={handleRejectCancellation} />}

      {user?.role === 'sponsor' && canCancel && !isCancellationPending && (
        <Button data-testid="sponsor-cancel-btn" variant="ghost" onClick={handleCancel} className="text-xs h-7 text-gray-400 hover:text-red-500">{t('cancel_request')}</Button>
      )}

      {user?.role === 'sponsor' && isCancellationPending && <CancellationPendingBadge />}

      {hasPerm('delete_requests') && <DeleteRequestButton onDelete={handleDeleteRequest} />}

      {isDelivered && <DeliveryConfirmedBadge request={request} />}

      {canConfirmDelivery && <DeliveryPanel deliveryNote={deliveryNote} onDeliveryNoteChange={setDeliveryNote} onConfirmDelivery={handleConfirmDelivery} confirming={confirmingDelivery} />}

      {user?.role === 'sponsor' && isPendingSponsorApproval && <SponsorApprovalPanel internalNote={internalNote} onNoteChange={setInternalNote} onApprove={handleSponsorApprove} />}

      <ReviewNotesCard items={request.items} internalNote={request.internal_note} />

      <HistoryCard history={request.history} />

      <ConfirmDialog open={!!confirmAction} onCancel={() => setConfirmAction(null)} onConfirm={() => confirmAction?.fn()}
        title={confirmAction?.title} description={confirmAction?.desc} confirmText={confirmAction?.confirmText} destructive={confirmAction?.destructive} />
    </div>
  );
}
